import React from "react";
import { Link } from "react-router-dom";
import FinlyLogo from "@/components/FinlyLogo";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#FBF7F0] text-[#0F172A] font-body antialiased" data-testid="privacy-page">
      <nav className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="privacy-nav-logo">
            <FinlyLogo size={32} rounded="22%" />
            <span className="font-display font-semibold text-base">Finly+</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-[#5C6B3A] hover:text-[#0F172A] transition-colors"
            data-testid="privacy-back-home"
          >
            ← Volver al inicio
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 py-14">
        <header className="mb-10">
          <p className="text-xs uppercase tracking-[0.22em] text-[#8A9A5B] mb-3">Legal</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Política de Privacidad</h1>
          <p className="mt-3 text-sm text-[#64748B]">Última actualización: 1 de junio de 2026</p>
        </header>

        <section className="prose-finly space-y-8 text-[15px] leading-relaxed text-[#1F2937]">
          <div>
            <h2 className="text-xl font-semibold mb-2">1. Quiénes somos</h2>
            <p>
              Finly+ es una aplicación de finanzas personales operada por <strong>Jabes Avalúos</strong>,
              disponible en <strong>finly.uno</strong>. En esta política te explicamos qué datos
              recolectamos, para qué los usamos y qué derechos tienes sobre ellos.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">2. Datos que recolectamos</h2>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>De tu cuenta:</strong> nombre, correo electrónico, contraseña cifrada.</li>
              <li><strong>Financieros que tú registras:</strong> transacciones, categorías, presupuestos, notas, cuentas y saldos.</li>
              <li><strong>Multimedia que tú subes:</strong> fotos de tickets (OCR) y grabaciones de voz (transcripción).</li>
              <li><strong>De pago:</strong> los procesa <a className="text-[#5C6B3A] underline" href="https://stripe.com" target="_blank" rel="noreferrer">Stripe</a>. Nosotros nunca almacenamos tu tarjeta — sólo guardamos un identificador de cliente y el estado de suscripción.</li>
              <li><strong>De uso anónimo:</strong> métricas agregadas vía PostHog (clicks, páginas vistas) para mejorar el producto.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">3. Para qué los usamos</h2>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Prestarte el Servicio (mostrar tus transacciones, generar reportes, sugerirte categorías).</li>
              <li>Procesar voz y OCR con proveedores AI (OpenAI Whisper, GPT-4o-mini, Google Gemini 2.5).</li>
              <li>Enviarte correos transaccionales (verificación, recibos, fin de prueba) a través de Resend.</li>
              <li>Detectar y prevenir fraude o uso indebido del Servicio.</li>
              <li>Mejorar el producto a partir de métricas agregadas y anónimas.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">4. Con quién los compartimos</h2>
            <p>Compartimos datos únicamente con los proveedores estrictamente necesarios para operar el Servicio:</p>
            <ul className="list-disc pl-6 space-y-1.5 mt-2">
              <li><strong>Stripe</strong> — procesamiento de pagos.</li>
              <li><strong>Resend</strong> — envío de correos transaccionales.</li>
              <li><strong>OpenAI</strong> y <strong>Google (Gemini)</strong> — procesamiento de voz y OCR.</li>
              <li><strong>MongoDB Atlas</strong> — almacenamiento cifrado de tus datos.</li>
              <li><strong>PostHog</strong> — analíticas anónimas de producto.</li>
            </ul>
            <p className="mt-2"><strong>Nunca</strong> vendemos tus datos personales ni financieros a terceros.</p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">5. Dónde se guardan</h2>
            <p>
              Tus datos se almacenan cifrados en MongoDB Atlas (servidores en Estados Unidos).
              Las comunicaciones entre tu dispositivo y nuestros servidores viajan siempre por HTTPS.
              Las contraseñas se almacenan únicamente con un hash bcrypt.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">6. Por cuánto tiempo</h2>
            <p>
              Conservamos tus datos mientras tu cuenta esté activa. Si cierras tu cuenta, eliminamos
              tu información personal en un máximo de 90 días, salvo lo que estemos obligados a conservar
              por ley (por ejemplo, registros contables o fiscales relacionados con suscripciones).
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">7. Tus derechos</h2>
            <p>En cualquier momento puedes:</p>
            <ul className="list-disc pl-6 space-y-1.5 mt-2">
              <li>Acceder a tus datos desde la app.</li>
              <li>Corregir, exportar o eliminar tu información solicitándolo a <a className="text-[#5C6B3A] underline" href="mailto:hola@finly.uno">hola@finly.uno</a>.</li>
              <li>Retirar tu consentimiento o cerrar tu cuenta.</li>
              <li>Presentar una queja ante la autoridad de protección de datos correspondiente.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">8. Cookies y tecnologías similares</h2>
            <p>
              Usamos cookies estrictamente necesarias para mantener tu sesión iniciada y para
              análisis anónimo de uso. No usamos cookies publicitarias ni de seguimiento entre sitios.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">9. Menores</h2>
            <p>
              El Servicio no está dirigido a menores de 13 años. Si crees que un menor de edad nos ha
              proporcionado datos, contáctanos para eliminarlos.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">10. Cambios en esta política</h2>
            <p>
              Si actualizamos esta política te notificaremos por correo o desde la propia aplicación
              al menos 7 días antes de que los cambios entren en vigor.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">11. Contacto</h2>
            <p>
              Si tienes dudas sobre tus datos o esta política, escríbenos a{" "}
              <a className="text-[#5C6B3A] underline" href="mailto:hola@finly.uno">hola@finly.uno</a>.
            </p>
          </div>
        </section>

        <footer className="mt-14 pt-8 border-t border-[#E2E8F0] text-xs text-[#94A3B8] flex items-center justify-between">
          <span>© 2026 Finly+ · Jabes Avalúos</span>
          <Link to="/terminos" className="hover:text-[#0F172A] transition-colors" data-testid="privacy-link-terms">
            Términos y Condiciones →
          </Link>
        </footer>
      </article>
    </div>
  );
}
